import type { ReactEventHandler } from 'react';
import { HERO_MAP_ORIGINAL, largestVariantPath } from '@buzzmap/shared';
import generated from '../generated/responsive-assets.json';
import { categoryFallbacks, categoryIllustrations } from './categories';

/**
 * T104-FE: props per <img> costruite dal JSON generato da
 * `scripts/optimize-web-assets.mjs` (derivati WebP con hash nel nome).
 *
 * `src` e la variante piu grande, `srcSet` elenca tutte le varianti con
 * descrittori di larghezza e `width`/`height` sono quelli veri della variante
 * in `src` (stesse proporzioni dell'originale): il browser riserva lo spazio
 * prima del caricamento e il layout non salta.
 */

type Variant = { width: number; height: number; path: string };
type Asset = { width: number; height: number; variants: Variant[] };
const assets = (generated as unknown as { assets: Record<string, Asset> }).assets;

export const LOGO_URL = '/logo_h.png';
const DEFAULT_CATEGORY = 'altro';

export interface BrandImageProps {
  src: string;
  srcSet?: string;
  sizes?: string;
  width?: number;
  height?: number;
  onError: ReactEventHandler<HTMLImageElement>;
}

/**
 * Torna all'originale UNA sola volta: se `src` e gia l'originale (secondo
 * errore, o errore dell'originale stesso) non fa nulla, quindi nessun ciclo.
 * `srcset` va tolto, altrimenti il browser continuerebbe a preferirlo a `src`.
 */
export function fallbackToOriginalOnce(img: HTMLImageElement, original: string): void {
  if (img.getAttribute('src') === original) return;
  img.removeAttribute('srcset');
  img.removeAttribute('sizes');
  img.setAttribute('src', original);
}

/** Asset del manifest con la sua variante piu grande; undefined se l'originale e sconosciuto. */
function findAsset(original: string): { asset: Asset; largest: Variant } | undefined {
  const asset = Object.hasOwn(assets, original) ? assets[original] : undefined;
  const largest = asset?.variants[asset.variants.length - 1];
  return asset && largest ? { asset, largest } : undefined;
}

/** Props per un originale del manifest; se l'asset e sconosciuto resta l'originale. */
export function brandImage(original: string, sizes: string): BrandImageProps {
  const onError: ReactEventHandler<HTMLImageElement> = (event) =>
    fallbackToOriginalOnce(event.currentTarget, original);
  const found = findAsset(original);
  if (!found) return { src: original, onError };
  const { asset, largest } = found;
  return {
    src: largest.path,
    srcSet: asset.variants.map((v) => `${v.path} ${v.width}w`).join(', '),
    sizes,
    width: largest.width,
    height: largest.height,
    onError,
  };
}

const lookup = (map: Record<string, string>, category: string): string =>
  Object.hasOwn(map, category) ? map[category]! : map[DEFAULT_CATEGORY]!;

/** Illustrazione della categoria (mapping di `categories.ts`; sconosciuta -> altro). */
export const illustrationImage = (category: string, sizes: string): BrandImageProps =>
  brandImage(`/illustrations/${lookup(categoryIllustrations, category)}`, sizes);

/** Foto di ripiego della categoria (mapping di `categories.ts`; sconosciuta -> altro). */
export const fallbackImage = (category: string, sizes: string): BrandImageProps =>
  brandImage(`/fallbacks/${lookup(categoryFallbacks, category)}`, sizes);

/** Logo orizzontale: stessi file e stesse dimensioni ovunque, cambia solo `sizes`. */
export const logoImage = (sizes: string): BrandImageProps => brandImage(LOGO_URL, sizes);

/**
 * Valore CSS `url()` per sfondi: derivato piu grande, oppure l'originale se sconosciuto.
 * La regola `largestVariantPath` e quella condivisa con il preload della hero-map
 * (T205-FE: Vite per la home statica, SSR per i territori): stessa regola, stesso JSON.
 */
export function brandBackgroundImage(original: string): string {
  return `url("${largestVariantPath(generated, original) ?? original}")`;
}

/** Originale dello sfondo dei chip categoria della Home; il suo derivato e quello che si precarica. */
export const HERO_MAP_IMAGE = HERO_MAP_ORIGINAL;
